/**
 * ReferralCodeTrackingPage (FS-826) — per-affiliate referral codes +
 * attribution counts. Link-based referral tracking, partner-of-partner.
 *
 * Composition:
 *   - emerald -> sky gradient header.
 *   - Four KPI tiles: total referrals, active sub-affiliates, revenue
 *     shared YTD, top referrer.
 *   - Toolbar: filter by affiliate id + status + "Generate Code" CTA.
 *   - Create form (collapsible panel): affiliate combobox, optional vanity
 *     code with live availability pulse, revenue share % (clamped 0-30).
 *   - @tanstack/react-table grid: Affiliate, Code (monospace + copy), Share
 *     URL (copy link), Referred count (opens signup drawer), Active, Rev
 *     share %, Earnings to-date, Status, Deactivate.
 *   - Side drawer (inline panel) listing signups for the selected code.
 *
 * Brand: emerald headline gradient, zinc borders, 6-8 px rounding, light
 * theme only, no emojis, no competitor names, no orange / purple / dark.
 *
 * All data plumbing lives in `./api` + `./hooks`; this file is render +
 * state wiring only (no raw fetch — D6 guard).
 */
import {
	useCallback,
	useEffect,
	useMemo,
	useState,
	type FormEvent,
} from "react";
import {
	flexRender,
	getCoreRowModel,
	useReactTable,
	type ColumnDef,
} from "@tanstack/react-table";
import {
	VANITY_CODE_MAX,
	VANITY_CODE_MIN,
	buildShareUrl,
	clampRevenueSharePercent,
	computeKpis,
	filterRows,
	formatInteger,
	formatMoneyCents,
	formatPercent,
	formatStatus,
	isVanityCodeShape,
	percentToBps,
	validateAffiliateId,
	validateVanityCode,
	type ReferralCodeRow,
	type ReferralStatus,
} from "./api";
import {
	useReferralCodeCreate,
	useReferralCodeDeactivate,
	useReferralCodesList,
	useReferralSignups,
	useVanityCodeAvailability,
} from "./hooks";

// ---------------------------------------------------------------------------
// Style tokens — aligned with dash brand rules.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const KPI_CARD = `${CARD} flex flex-col gap-1 border-l-4 border-l-emerald-500 p-5`;
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const SELECT = INPUT;
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const BADGE_BASE =
	"inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";
const BADGE_ACTIVE = `${BADGE_BASE} bg-emerald-50 text-emerald-700`;
const BADGE_INACTIVE = `${BADGE_BASE} bg-zinc-100 text-zinc-600`;
const BADGE_EXPIRED = `${BADGE_BASE} bg-amber-50 text-amber-700`;

function statusBadgeClass(s: ReferralStatus): string {
	if (s === "active") return BADGE_ACTIVE;
	if (s === "expired") return BADGE_EXPIRED;
	return BADGE_INACTIVE;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ReferralCodeTrackingPage() {
	const listQuery = useReferralCodesList();
	const createMut = useReferralCodeCreate();
	const deactivateMut = useReferralCodeDeactivate();

	// ---- toolbar filters
	const [affiliateFilter, setAffiliateFilter] = useState("");
	const [statusFilter, setStatusFilter] = useState<"all" | ReferralStatus>(
		"all",
	);

	// ---- create form state
	const [formOpen, setFormOpen] = useState(false);
	const [formAffiliate, setFormAffiliate] = useState("");
	const [formVanity, setFormVanity] = useState("");
	const [formSharePct, setFormSharePct] = useState<number>(10);
	const [formError, setFormError] = useState<string | null>(null);

	// ---- signup drawer
	const [selectedRefId, setSelectedRefId] = useState<string | null>(null);
	const signupsQuery = useReferralSignups(selectedRefId);

	// ---- vanity availability check — only when shape is valid
	const vanityShapeOk = isVanityCodeShape(formVanity);
	const availabilityQuery = useVanityCodeAvailability(
		vanityShapeOk ? formVanity.trim() : null,
	);

	const items = listQuery.data?.items ?? [];
	const filtered = useMemo(
		() =>
			filterRows(items, {
				affiliate: affiliateFilter,
				status: statusFilter,
			}),
		[items, affiliateFilter, statusFilter],
	);
	const kpis = useMemo(() => computeKpis(items), [items]);

	// ---- submit handler
	const handleCreate = useCallback(
		(e: FormEvent) => {
			e.preventDefault();
			setFormError(null);

			const affiliateId = validateAffiliateId(formAffiliate);
			if (!affiliateId) {
				setFormError("Enter a valid affiliate id.");
				return;
			}

			let vanity: string | undefined;
			if (formVanity.trim()) {
				const v = validateVanityCode(formVanity);
				if (!v) {
					setFormError(
						`Vanity code must be ${VANITY_CODE_MIN}-${VANITY_CODE_MAX} alphanumeric characters.`,
					);
					return;
				}
				if (availabilityQuery.data && !availabilityQuery.data.available) {
					setFormError("That code is already taken. Try another.");
					return;
				}
				vanity = v;
			}

			const pct = clampRevenueSharePercent(formSharePct);

			createMut.mutate(
				{
					affiliate_id: affiliateId,
					code: vanity,
					revenue_share_bps: percentToBps(pct),
				},
				{
					onSuccess: () => {
						setFormOpen(false);
						setFormAffiliate("");
						setFormVanity("");
						setFormSharePct(10);
					},
				},
			);
		},
		[formAffiliate, formVanity, formSharePct, availabilityQuery.data, createMut],
	);

	// ---- table
	const columns = useMemo<ColumnDef<ReferralCodeRow>[]>(
		() => [
			{
				id: "affiliate",
				header: "Affiliate",
				accessorKey: "affiliate_id",
				cell: (ctx) => (
					<span className="text-sm font-medium text-zinc-900">
						{ctx.getValue<string>()}
					</span>
				),
			},
			{
				id: "code",
				header: "Referral code",
				accessorKey: "code",
				cell: (ctx) => (
					<code
						className="rounded-[4px] bg-zinc-50 px-2 py-0.5 font-mono text-xs text-zinc-900"
						data-testid={`referral-code-value-${ctx.row.original.id}`}
					>
						{ctx.getValue<string>()}
					</code>
				),
			},
			{
				id: "share_url",
				header: "Share URL",
				cell: (ctx) => (
					<CopyUrlButton code={ctx.row.original.code} />
				),
			},
			{
				id: "referred",
				header: "Referred",
				accessorKey: "referred_count",
				cell: (ctx) => (
					<button
						type="button"
						className="rounded-[4px] px-1 text-sm font-medium text-emerald-700 underline-offset-2 hover:underline"
						onClick={() => setSelectedRefId(ctx.row.original.id)}
						data-testid={`referred-count-${ctx.row.original.id}`}
					>
						{formatInteger(ctx.row.original.referred_count)}
					</button>
				),
			},
			{
				id: "active",
				header: "Active",
				accessorKey: "active_count",
				cell: (ctx) => (
					<span className="tabular-nums text-sm text-zinc-700">
						{formatInteger(ctx.row.original.active_count)}
					</span>
				),
			},
			{
				id: "revenue_share",
				header: "Rev share",
				accessorKey: "revenue_share_bps",
				cell: (ctx) => (
					<span className="tabular-nums text-sm text-zinc-700">
						{formatPercent(ctx.row.original.revenue_share_bps)}
					</span>
				),
			},
			{
				id: "earnings",
				header: "Earnings",
				accessorKey: "revenue_shared_cents",
				cell: (ctx) => (
					<span className="tabular-nums text-sm text-zinc-900">
						{formatMoneyCents(ctx.row.original.revenue_shared_cents)}
					</span>
				),
			},
			{
				id: "status",
				header: "Status",
				accessorKey: "status",
				cell: (ctx) => {
					const s = ctx.getValue<ReferralStatus>();
					return (
						<span
							className={statusBadgeClass(s)}
							data-testid={`referral-status-${ctx.row.original.id}`}
						>
							{formatStatus(s)}
						</span>
					);
				},
			},
			{
				id: "actions",
				header: "",
				cell: (ctx) => (
					<button
						type="button"
						className={BTN_DANGER}
						onClick={() => deactivateMut.mutate(ctx.row.original.id)}
						disabled={
							deactivateMut.isPending || ctx.row.original.status !== "active"
						}
						data-testid={`referral-deactivate-${ctx.row.original.id}`}
					>
						Deactivate
					</button>
				),
			},
		],
		[deactivateMut],
	);

	const table = useReactTable({
		data: filtered,
		columns,
		getCoreRowModel: getCoreRowModel(),
	});

	return (
		<main
			data-testid="referral-code-tracking-page"
			className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-6 py-10"
		>
			<Header />

			<KpiTiles
				kpis={kpis}
				loading={listQuery.isLoading}
			/>

			<Toolbar
				affiliate={affiliateFilter}
				onAffiliateChange={setAffiliateFilter}
				status={statusFilter}
				onStatusChange={setStatusFilter}
				onGenerate={() => setFormOpen((v) => !v)}
				formOpen={formOpen}
			/>

			{formOpen ? (
				<CreateForm
					affiliate={formAffiliate}
					setAffiliate={setFormAffiliate}
					vanity={formVanity}
					setVanity={setFormVanity}
					sharePct={formSharePct}
					setSharePct={setFormSharePct}
					availability={
						vanityShapeOk ? availabilityQuery.data : undefined
					}
					checking={vanityShapeOk && availabilityQuery.isLoading}
					onSubmit={handleCreate}
					onCancel={() => {
						setFormOpen(false);
						setFormError(null);
					}}
					busy={createMut.isPending}
					error={formError ?? createMut.error?.message ?? null}
				/>
			) : null}

			<TableCard
				loading={listQuery.isLoading}
				error={listQuery.error?.message ?? null}
				rowCount={items.length}
				filteredCount={filtered.length}
				table={table}
			/>

			{selectedRefId ? (
				<SignupsDrawer
					referralId={selectedRefId}
					loading={signupsQuery.isLoading}
					error={signupsQuery.error?.message ?? null}
					rows={signupsQuery.data?.items ?? []}
					onClose={() => setSelectedRefId(null)}
				/>
			) : null}
		</main>
	);
}

export default ReferralCodeTrackingPage;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
				Affiliates
			</p>
			<h1 className="bg-gradient-to-r from-emerald-500 to-sky-500 bg-clip-text text-3xl font-semibold tracking-tight text-transparent sm:text-4xl">
				Referral codes
			</h1>
			<p className="max-w-2xl text-sm text-zinc-600">
				One code per affiliate maps a shareable URL to everyone who signs
				up through it. Activity shown here is the partner-of-partner
				attribution chain — clicks, signups, active seats and revenue
				shared back to the referrer.
			</p>
		</header>
	);
}

function KpiTiles(props: {
	kpis: ReturnType<typeof computeKpis>;
	loading: boolean;
}) {
	const cells: {
		label: string;
		value: string;
		testid: string;
		hint?: string;
	}[] = [
		{
			label: "Total referrals",
			value: props.loading ? "—" : formatInteger(props.kpis.total_referrals),
			testid: "kpi-total-referrals",
		},
		{
			label: "Active sub-affiliates",
			value: props.loading
				? "—"
				: formatInteger(props.kpis.active_sub_affiliates),
			testid: "kpi-active-sub-affiliates",
		},
		{
			label: "Revenue shared YTD",
			value: props.loading
				? "—"
				: formatMoneyCents(props.kpis.revenue_shared_cents),
			testid: "kpi-revenue-shared-ytd",
		},
		{
			label: "Top referrer",
			value: props.loading
				? "—"
				: props.kpis.top_referrer_id
					? `${props.kpis.top_referrer_id} (${formatInteger(props.kpis.top_referrer_count)})`
					: "None yet",
			testid: "kpi-top-referrer",
		},
	];

	return (
		<section
			aria-label="Referral program KPIs"
			data-testid="kpi-tiles"
			className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
		>
			{cells.map((c) => (
				<div
					key={c.label}
					className={KPI_CARD}
					data-testid={c.testid}
				>
					<p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
						{c.label}
					</p>
					<p className="mt-2 text-2xl font-semibold tabular-nums text-zinc-900">
						{c.value}
					</p>
				</div>
			))}
		</section>
	);
}

function Toolbar(props: {
	affiliate: string;
	onAffiliateChange: (v: string) => void;
	status: "all" | ReferralStatus;
	onStatusChange: (v: "all" | ReferralStatus) => void;
	onGenerate: () => void;
	formOpen: boolean;
}) {
	return (
		<section
			className={`${CARD} flex flex-col gap-3 p-4 sm:flex-row sm:items-end sm:justify-between`}
			data-testid="referral-toolbar"
		>
			<div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-end">
				<div className="flex flex-1 flex-col gap-1">
					<label className={LABEL} htmlFor="filter-affiliate">
						Affiliate filter
					</label>
					<input
						id="filter-affiliate"
						type="text"
						className={INPUT}
						placeholder="e.g. pub_42"
						value={props.affiliate}
						onChange={(e) => props.onAffiliateChange(e.currentTarget.value)}
						data-testid="filter-affiliate-input"
					/>
				</div>
				<div className="flex flex-col gap-1 sm:w-48">
					<label className={LABEL} htmlFor="filter-status">
						Status
					</label>
					<select
						id="filter-status"
						className={SELECT}
						value={props.status}
						onChange={(e) =>
							props.onStatusChange(
								e.currentTarget.value as "all" | ReferralStatus,
							)
						}
						data-testid="filter-status-select"
					>
						<option value="all">All</option>
						<option value="active">Active</option>
						<option value="inactive">Inactive</option>
						<option value="expired">Expired</option>
					</select>
				</div>
			</div>
			<button
				type="button"
				className={BTN_PRIMARY}
				onClick={props.onGenerate}
				data-testid="generate-code-btn"
			>
				{props.formOpen ? "Close" : "Generate code"}
			</button>
		</section>
	);
}

function CreateForm(props: {
	affiliate: string;
	setAffiliate: (v: string) => void;
	vanity: string;
	setVanity: (v: string) => void;
	sharePct: number;
	setSharePct: (v: number) => void;
	availability: { code: string; available: boolean } | undefined;
	checking: boolean;
	onSubmit: (e: FormEvent) => void;
	onCancel: () => void;
	busy: boolean;
	error: string | null;
}) {
	const vanityShapeOk = isVanityCodeShape(props.vanity);
	const touched = props.vanity.trim().length > 0;
	let availHint = "";
	let availClass = "text-zinc-500";
	if (touched && !vanityShapeOk) {
		availHint = `${VANITY_CODE_MIN}-${VANITY_CODE_MAX} letters / digits only.`;
		availClass = "text-red-600";
	} else if (touched && props.checking) {
		availHint = "Checking availability.";
		availClass = "text-zinc-500 animate-pulse";
	} else if (touched && vanityShapeOk && props.availability) {
		if (props.availability.available) {
			availHint = "Available.";
			availClass = "text-emerald-600";
		} else {
			availHint = "Taken.";
			availClass = "text-red-600";
		}
	}

	return (
		<section
			className={`${CARD} p-5`}
			data-testid="create-form-card"
			aria-label="Generate referral code"
		>
			<h2 className="text-base font-semibold text-zinc-900">
				Generate a new referral code
			</h2>
			<p className="mt-1 text-sm text-zinc-500">
				A code maps exactly one affiliate to a shareable sign-up URL. Vanity
				codes are optional; leave blank for a random slug.
			</p>
			<form
				onSubmit={props.onSubmit}
				className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3"
				data-testid="create-form"
			>
				<div className="flex flex-col gap-1 sm:col-span-1">
					<label className={LABEL} htmlFor="form-affiliate">
						Affiliate id
					</label>
					<input
						id="form-affiliate"
						type="text"
						className={INPUT}
						placeholder="pub_42"
						value={props.affiliate}
						onChange={(e) => props.setAffiliate(e.currentTarget.value)}
						disabled={props.busy}
						data-testid="form-affiliate-input"
					/>
				</div>

				<div className="flex flex-col gap-1 sm:col-span-1">
					<label className={LABEL} htmlFor="form-vanity">
						Vanity code (optional)
					</label>
					<input
						id="form-vanity"
						type="text"
						className={INPUT}
						placeholder="spring26"
						value={props.vanity}
						onChange={(e) => props.setVanity(e.currentTarget.value)}
						disabled={props.busy}
						data-testid="form-vanity-input"
						maxLength={VANITY_CODE_MAX + 2}
					/>
					<p
						className={`text-xs ${availClass}`}
						data-testid="form-vanity-hint"
						aria-live="polite"
					>
						{availHint || `${VANITY_CODE_MIN}-${VANITY_CODE_MAX} letters / digits.`}
					</p>
				</div>

				<div className="flex flex-col gap-1 sm:col-span-1">
					<label className={LABEL} htmlFor="form-share">
						Revenue share %
					</label>
					<input
						id="form-share"
						type="number"
						min={0}
						max={30}
						step={1}
						className={INPUT}
						value={props.sharePct}
						onChange={(e) =>
							props.setSharePct(
								clampRevenueSharePercent(Number(e.currentTarget.value)),
							)
						}
						disabled={props.busy}
						data-testid="form-share-input"
					/>
					<p className="text-xs text-zinc-500">
						Clamped 0-30 % of downstream revenue.
					</p>
				</div>

				<div className="sm:col-span-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
					{props.error ? (
						<p
							className="text-sm text-red-600"
							role="alert"
							data-testid="form-error"
						>
							{props.error}
						</p>
					) : null}
					<div className="flex items-center gap-2">
						<button
							type="button"
							className={BTN_NEUTRAL}
							onClick={props.onCancel}
							disabled={props.busy}
							data-testid="form-cancel"
						>
							Cancel
						</button>
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={props.busy}
							data-testid="form-submit"
						>
							{props.busy ? "Saving." : "Generate"}
						</button>
					</div>
				</div>
			</form>
		</section>
	);
}

function TableCard(props: {
	loading: boolean;
	error: string | null;
	rowCount: number;
	filteredCount: number;
	// biome-ignore lint/suspicious/noExplicitAny: TanStack table instance type.
	table: any;
}) {
	return (
		<section
			className={`${CARD} flex flex-col`}
			data-testid="referral-table-card"
			aria-label="Referral codes"
		>
			<header className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
				<div>
					<h2 className="text-base font-semibold text-zinc-900">
						Referral codes
					</h2>
					<p className="text-xs text-zinc-500">
						{props.loading
							? "Loading."
							: props.rowCount === props.filteredCount
								? `${formatInteger(props.rowCount)} total`
								: `${formatInteger(props.filteredCount)} of ${formatInteger(props.rowCount)} shown`}
					</p>
				</div>
			</header>

			{props.error ? (
				<div className="p-5 text-sm text-red-600" role="alert">
					{props.error}
				</div>
			) : props.loading ? (
				<p
					className="p-5 text-sm text-zinc-500"
					data-testid="referral-code-tracking-loading"
				>
					Loading.
				</p>
			) : props.rowCount === 0 ? (
				<div
					className="p-10 text-center text-sm text-zinc-500"
					data-testid="referral-code-tracking-empty"
				>
					<p className="font-medium text-zinc-700">No referral codes yet.</p>
					<p className="mt-1">
						Click "Generate code" above to mint one for an affiliate.
					</p>
				</div>
			) : props.filteredCount === 0 ? (
				<div
					className="p-10 text-center text-sm text-zinc-500"
					data-testid="referral-code-tracking-no-match"
				>
					No codes match the current filters.
				</div>
			) : (
				<div className="overflow-x-auto">
					<table
						className="w-full border-collapse text-sm"
						data-testid="referral-code-tracking-list"
					>
						<thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
							{props.table.getHeaderGroups().map(
								// biome-ignore lint/suspicious/noExplicitAny: header group.
								(hg: any) => (
									<tr key={hg.id}>
										{hg.headers.map(
											// biome-ignore lint/suspicious/noExplicitAny: header cell.
											(h: any) => (
												<th
													key={h.id}
													className="px-4 py-3 text-left font-medium"
												>
													{h.isPlaceholder
														? null
														: flexRender(
																h.column.columnDef.header,
																h.getContext(),
															)}
												</th>
											),
										)}
									</tr>
								),
							)}
						</thead>
						<tbody>
							{props.table.getRowModel().rows.map(
								// biome-ignore lint/suspicious/noExplicitAny: row.
								(r: any) => (
									<tr
										key={r.id}
										className="transition-colors hover:bg-emerald-50/40"
										data-testid={`referral-code-tracking-row-${r.original.id}`}
									>
										{r.getVisibleCells().map(
											// biome-ignore lint/suspicious/noExplicitAny: cell.
											(cell: any) => (
												<td
													key={cell.id}
													className="border-t border-zinc-200 px-4 py-3"
												>
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext(),
													)}
												</td>
											),
										)}
									</tr>
								),
							)}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

function CopyUrlButton({ code }: { code: string }) {
	const [copied, setCopied] = useState(false);
	const url = useMemo(() => buildShareUrl(code), [code]);
	const onCopy = useCallback(async () => {
		try {
			const nav = (globalThis as { navigator?: Navigator }).navigator;
			if (nav?.clipboard?.writeText) {
				await nav.clipboard.writeText(url);
			}
			setCopied(true);
		} catch {
			setCopied(true);
		}
	}, [url]);

	useEffect(() => {
		if (!copied) return;
		const t = setTimeout(() => setCopied(false), 1500);
		return () => clearTimeout(t);
	}, [copied]);

	return (
		<button
			type="button"
			className={`${BTN_NEUTRAL} inline-flex items-center gap-2`}
			onClick={onCopy}
			data-testid={`copy-share-url-${code}`}
			title={url}
		>
			<span className="font-mono text-xs text-zinc-500">
				{url.length > 32 ? `${url.slice(0, 29)}.` : url}
			</span>
			<span className="text-xs font-semibold text-emerald-700">
				{copied ? "Copied" : "Copy"}
			</span>
		</button>
	);
}

function SignupsDrawer(props: {
	referralId: string;
	loading: boolean;
	error: string | null;
	rows: {
		id: string;
		account_id: string;
		email?: string | null;
		status: "active" | "churned" | "pending";
		revenue_shared_cents?: number;
		created_at: number;
	}[];
	onClose: () => void;
}) {
	return (
		<section
			className={`${CARD} flex flex-col`}
			data-testid="signups-drawer"
			aria-label="Referred signups"
		>
			<header className="flex items-center justify-between border-b border-zinc-200 p-4">
				<div>
					<h2 className="text-base font-semibold text-zinc-900">
						Referred signups
					</h2>
					<p className="text-xs text-zinc-500">
						Accounts attributed to this referral code.
					</p>
				</div>
				<button
					type="button"
					className={BTN_NEUTRAL}
					onClick={props.onClose}
					data-testid="signups-drawer-close"
				>
					Close
				</button>
			</header>
			{props.error ? (
				<div className="p-5 text-sm text-red-600" role="alert">
					{props.error}
				</div>
			) : props.loading ? (
				<p className="p-5 text-sm text-zinc-500">Loading.</p>
			) : props.rows.length === 0 ? (
				<p
					className="p-5 text-sm text-zinc-500"
					data-testid="signups-empty"
				>
					No signups yet for this code.
				</p>
			) : (
				<div className="overflow-x-auto">
					<table
						className="w-full text-left text-sm"
						data-testid="signups-table"
					>
						<thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
							<tr>
								<th className="px-4 py-3 font-medium">Account</th>
								<th className="px-4 py-3 font-medium">Email</th>
								<th className="px-4 py-3 font-medium">Status</th>
								<th className="px-4 py-3 font-medium">Earnings</th>
							</tr>
						</thead>
						<tbody>
							{props.rows.map((r) => (
								<tr
									key={r.id}
									className="border-t border-zinc-200"
									data-testid={`signup-row-${r.id}`}
								>
									<td className="px-4 py-3 font-mono text-xs text-zinc-700">
										{r.account_id}
									</td>
									<td className="px-4 py-3 text-zinc-700">
										{r.email || "—"}
									</td>
									<td className="px-4 py-3">
										<span
											className={
												r.status === "active"
													? BADGE_ACTIVE
													: r.status === "pending"
														? BADGE_EXPIRED
														: BADGE_INACTIVE
											}
										>
											{r.status}
										</span>
									</td>
									<td className="px-4 py-3 tabular-nums text-zinc-900">
										{formatMoneyCents(r.revenue_shared_cents)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}
